import { PaletteMode } from "@mui/material";
import { alpha, createTheme, ThemeOptions } from "@mui/material/styles";
import { dsDestructive, dsSurface } from "@Themes/palettes/app-palette";
import { vrBase, vrNeutralDark, vrNeutralLight, vrSurfaceDark } from "@Themes/palettes/vr-palette";
import { sharedPalette, vrPaletteDark, vrPaletteLight } from "@Themes/themes.config";
import "@Themes/typography/mui-typography-extension";
import { sharedTypography, typographyDefaultProps } from "@Themes/typography/typography";
import {
  vrButtonOverride,
  vrCardOverride,
  vrChipOverride,
  vrMenuOverride,
  vrMenuItemOverride,
  vrPaperOverride,
  vrToggleButtonGroupOverride,
} from "./components";
import { vrDialogContentOverride, vrDialogOverride, vrDialogTitleOverride } from "./components/dialog";

const getThemeConfig = (mode: PaletteMode): ThemeOptions => {
  const isLightMode = mode === "light";
  const palette = isLightMode ? vrPaletteLight : vrPaletteDark;
  const surfaceOverlay = {
    background: alpha(palette.dsSurface.v200, 0.5),
    border: alpha(palette.dsBase.v200, 0.1),
  };

  return {
    typography: sharedTypography,
    components: {
      MuiDialog: vrDialogOverride,
      MuiButton: vrButtonOverride,
      MuiChip: vrChipOverride,
      MuiMenu: vrMenuOverride,
      MuiMenuItem: vrMenuItemOverride,
      MuiToggleButtonGroup: vrToggleButtonGroupOverride,
      MuiDialogTitle: vrDialogTitleOverride,
      MuiDialogContent: vrDialogContentOverride,
      MuiTypography: {
        defaultProps: typographyDefaultProps,
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            padding: "12px 16px",
            fontWeight: 400,
            fontFamily: "Inter",
            fontSize: "16px",
            lineHeight: "24px",
            cornerShape: "squircle",
            borderRadius: "32px",
          },
          standardInfo: {
            border: "1px solid",
            ...(isLightMode
              ? {
                  background: dsSurface.v200,
                  color: vrBase.v200,
                  borderColor: dsSurface.v200,
                }
              : {
                  background: vrSurfaceDark.v100,
                  color: vrBase.v100,
                  borderColor: vrSurfaceDark.v300,
                }),
          },
          standardError: {
            background: dsDestructive.v200,
            color: dsDestructive.v400,
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            borderRadius: "unset",
          },
        },
      },
      MuiCard: vrCardOverride,
      MuiPaper: vrPaperOverride,
    },
    palette: {
      mode,
      ...sharedPalette,
      ...palette,
      surfaceOverlay,
      common: {
        white: vrBase.v100,
        black: vrBase.v200,
      },
      tertiary: {
        main: vrSurfaceDark.v400,
        dark: vrNeutralDark.v100,
        contrastText: vrBase.v100,
      },
      text: {
        primary: isLightMode ? vrBase.v200 : vrBase.v100,
        secondary: isLightMode ? vrNeutralLight.v400 : vrNeutralDark.v300,
        tertiary: isLightMode ? vrNeutralLight.v200 : vrNeutralDark.v100,
      },
    },
  };
};

const getTheme = (mode: PaletteMode) => createTheme(getThemeConfig(mode));

export { getTheme };
